import { CommonModule } from '@angular/common';
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectorRef, Component, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import type { BrandSummary } from '../../models/brand.models';
import type {
  CreateSenderIdentityPayload,
  SenderIdentityNotificationKey,
  SenderIdentityNotificationSettings,
  SenderIdentitySummary,
  UpdateSenderIdentityPayload,
} from '../../models/sender-identity.models';
import { BrandApiService } from '../../services/brand-api.service';
import { SenderIdentityApiService } from '../../services/sender-identity-api.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type SenderIdentityModalMode = 'add' | 'edit';

interface SenderIdentityNotificationOption {
  key: SenderIdentityNotificationKey;
  label: string;
}

const SENDER_IDENTITY_NOTIFICATION_OPTIONS: readonly SenderIdentityNotificationOption[] = [
  { key: 'recipientViewsDocument', label: 'Recipient views the document' },
  { key: 'documentHasBeenSent', label: 'Document has been sent' },
  { key: 'emailDeliveryFailedForRecipient', label: 'Email delivery failed for a recipient' },
  { key: 'recipientDeclinesToSign', label: 'Recipient declines to sign' },
  { key: 'documentHasBeenRevoked', label: 'Document has been revoked' },
  { key: 'authenticationFailsForDocument', label: 'Authentication fails for a document' },
  { key: 'recipientSignsOrApprovesDocument', label: 'Recipient signs or approves a document' },
  { key: 'documentHasExpired', label: 'Document has expired' },
  { key: 'remindersOfDocument', label: 'Reminders of the document' },
  { key: 'documentCompletedByAllRecipients', label: 'Document has been completed by all recipients' },
] as const;

const createDefaultNotificationSettings = (): SenderIdentityNotificationSettings => ({
  enabled: true,
  notifications: {
    recipientViewsDocument: false,
    documentHasBeenSent: false,
    emailDeliveryFailedForRecipient: false,
    recipientDeclinesToSign: false,
    documentHasBeenRevoked: false,
    authenticationFailsForDocument: false,
    recipientSignsOrApprovesDocument: false,
    documentHasExpired: false,
    remindersOfDocument: false,
    documentCompletedByAllRecipients: false,
  },
});

@Component({
  selector: 'app-sender-identities',
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: './sender-identities.component.html',
  styleUrl: './sender-identities.component.scss',
})
export class SenderIdentitiesComponent implements OnInit {
  readonly notificationOptions = SENDER_IDENTITY_NOTIFICATION_OPTIONS;
  readonly menuPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -6,
    },
  ];

  senderIdentities: SenderIdentitySummary[] = [];
  brands: BrandSummary[] = [];

  searchQuery = '';
  feedbackMessage = '';
  errorMessage = '';
  loading = false;
  brandsLoading = false;

  identitiesCountLabel(): string {
    return this.filteredSenderIdentities.length === 1 ? 'Sender Identity' : 'Sender Identities';
  }

  clearSearch(): void {
    this.searchQuery = '';
  }
  modalMode: SenderIdentityModalMode | null = null;
  saving = false;
  modalErrorMessage = '';
  openMenuIdentityId: string | null = null;
  editTarget: SenderIdentitySummary | null = null;
  deleteTarget: SenderIdentitySummary | null = null;
  notificationTarget: SenderIdentitySummary | null = null;
  deleting = false;
  resendingIdentityId: string | null = null;
  notificationLoading = false;
  notificationSaving = false;
  notificationErrorMessage = '';
  notificationSettings = createDefaultNotificationSettings();
  private notificationSnapshot = JSON.stringify(createDefaultNotificationSettings());
  form = {
    name: '',
    email: '',
    brandId: '',
  };

  private readonly api = inject(SenderIdentityApiService);
  private readonly brandsApi = inject(BrandApiService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadSenderIdentities();
    this.loadBrands();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('[data-sender-identity-menu]')) {
      return;
    }

    this.openMenuIdentityId = null;
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.deleteTarget) {
      this.closeDeleteConfirm();
      return;
    }

    if (this.notificationModalOpen) {
      this.closeNotificationModal();
      return;
    }

    if (this.modalOpen) {
      this.closeModal();
      return;
    }

    this.openMenuIdentityId = null;
  }

  get filteredSenderIdentities(): SenderIdentitySummary[] {
    const query = this.searchQuery.trim().toLowerCase();
    if (!query) {
      return this.senderIdentities;
    }

    return this.senderIdentities.filter((identity) => {
      return identity.name.toLowerCase().includes(query) || identity.email.toLowerCase().includes(query);
    });
  }

  get modalOpen(): boolean {
    return this.modalMode !== null;
  }

  get canSubmit(): boolean {
    return (
      !this.saving &&
      this.form.name.trim().length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email.trim())
    );
  }

  get canUpdate(): boolean {
    if (this.saving || !this.editTarget) {
      return false;
    }

    const normalizedName = this.form.name.trim().replace(/\s+/g, ' ');
    if (!normalizedName) {
      return false;
    }

    if (normalizedName === this.editTarget.name) {
      return this.normalizedBrandId() !== this.resolveBrandId(this.editTarget);
    }

    return true;
  }

  get notificationModalOpen(): boolean {
    return this.notificationTarget !== null;
  }

  get notificationInputsDisabled(): boolean {
    return this.notificationLoading || this.notificationSaving || !this.notificationSettings.enabled;
  }

  get canSaveNotificationSettings(): boolean {
    if (!this.notificationTarget || this.notificationLoading || this.notificationSaving) {
      return false;
    }

    return JSON.stringify(this.notificationSettings) !== this.notificationSnapshot;
  }

  get allNotificationsSelected(): boolean {
    return this.notificationOptions.every((option) => this.notificationSettings.notifications[option.key]);
  }

  get someNotificationsSelected(): boolean {
    return this.notificationOptions.some((option) => this.notificationSettings.notifications[option.key]);
  }

  formatDateTime(value: string | null | undefined): string {
    return value ? this.dateTimeFormat.formatDateTime(value) : '-';
  }

  openAddModal(): void {
    this.modalMode = 'add';
    this.editTarget = null;
    this.modalErrorMessage = '';
    this.feedbackMessage = '';
    this.form = {
      name: '',
      email: '',
      brandId: '',
    };
  }

  closeModal(force = false): void {
    if (this.saving && !force) {
      return;
    }

    this.modalMode = null;
    this.editTarget = null;
    this.modalErrorMessage = '';
    this.form = {
      name: '',
      email: '',
      brandId: '',
    };
  }

  toggleMenu(event: MouseEvent, identityId: string): void {
    event.stopPropagation();
    this.openMenuIdentityId = this.openMenuIdentityId === identityId ? null : identityId;
  }

  closeMenu(): void {
    this.openMenuIdentityId = null;
  }

  isMenuOpen(identityId: string): boolean {
    return this.openMenuIdentityId === identityId;
  }

  openEditModal(identity: SenderIdentitySummary): void {
    this.openMenuIdentityId = null;
    this.modalMode = 'edit';
    this.editTarget = identity;
    this.modalErrorMessage = '';
    this.feedbackMessage = '';
    this.form = {
      name: identity.name,
      email: identity.email,
      brandId: this.resolveBrandId(identity),
    };
  }

  openDeleteConfirm(identity: SenderIdentitySummary): void {
    this.openMenuIdentityId = null;
    this.deleteTarget = identity;
    this.feedbackMessage = '';
    this.modalErrorMessage = '';
  }

  openNotificationModal(identity: SenderIdentitySummary): void {
    if (identity.status !== 'verified') {
      return;
    }

    this.openMenuIdentityId = null;
    this.notificationTarget = identity;
    this.notificationLoading = true;
    this.notificationSaving = false;
    this.notificationErrorMessage = '';
    this.feedbackMessage = '';
    this.notificationSettings = createDefaultNotificationSettings();
    this.notificationSnapshot = JSON.stringify(this.notificationSettings);

    this.api
      .getNotificationSettings(identity.id)
      .pipe(
        finalize(() => {
          this.notificationLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (settings) => {
          this.notificationSettings = this.cloneNotificationSettings(settings);
          this.notificationSnapshot = JSON.stringify(this.notificationSettings);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.notificationErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load email notification settings.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  resendVerification(identity: SenderIdentitySummary): void {
    if (identity.status !== 'pending' || this.resendingIdentityId) {
      return;
    }

    this.openMenuIdentityId = null;
    this.feedbackMessage = '';
    this.errorMessage = '';
    this.resendingIdentityId = identity.id;

    this.api
      .resendVerification(identity.id)
      .pipe(
        finalize(() => {
          this.resendingIdentityId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.feedbackMessage = `Verification email resent to ${identity.email}.`;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to resend verification email.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  closeDeleteConfirm(): void {
    if (this.deleting) {
      return;
    }

    this.deleteTarget = null;
  }

  closeNotificationModal(force = false): void {
    if ((this.notificationLoading || this.notificationSaving) && !force) {
      return;
    }

    this.notificationTarget = null;
    this.notificationLoading = false;
    this.notificationSaving = false;
    this.notificationErrorMessage = '';
    this.notificationSettings = createDefaultNotificationSettings();
    this.notificationSnapshot = JSON.stringify(this.notificationSettings);
  }

  setNotificationEnabled(enabled: boolean): void {
    this.notificationSettings = {
      ...this.notificationSettings,
      enabled,
    };
  }

  toggleAllNotifications(checked: boolean): void {
    const notifications = { ...this.notificationSettings.notifications };
    for (const option of this.notificationOptions) {
      notifications[option.key] = checked;
    }

    this.notificationSettings = {
      ...this.notificationSettings,
      notifications,
    };
  }

  updateNotificationPreference(key: SenderIdentityNotificationKey, checked: boolean): void {
    this.notificationSettings = {
      ...this.notificationSettings,
      notifications: {
        ...this.notificationSettings.notifications,
        [key]: checked,
      },
    };
  }

  submitNotificationSettings(): void {
    const identity = this.notificationTarget;
    if (!identity || !this.canSaveNotificationSettings) {
      return;
    }

    this.notificationSaving = true;
    this.notificationErrorMessage = '';

    this.api
      .updateNotificationSettings(identity.id, this.cloneNotificationSettings(this.notificationSettings))
      .pipe(
        finalize(() => {
          this.notificationSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (settings) => {
          this.notificationSettings = this.cloneNotificationSettings(settings);
          this.notificationSnapshot = JSON.stringify(this.notificationSettings);
          this.feedbackMessage = `Email notification settings saved for ${identity.email}.`;
          this.closeNotificationModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.notificationErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to save email notification settings.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  submitAddSenderIdentity(): void {
    if (!this.canSubmit) {
      return;
    }

    this.saving = true;
    this.modalErrorMessage = '';

    const payload: CreateSenderIdentityPayload = {
      name: this.form.name.trim(),
      email: this.form.email.trim(),
      brandId: this.form.brandId.trim() || undefined,
    };

    this.api
      .createSenderIdentity(payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (identity) => {
          this.senderIdentities = [identity, ...this.senderIdentities];
          this.feedbackMessage = `Verification email sent to ${identity.email}. The sender identity will remain pending until approved.`;
          this.closeModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.modalErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to add sender identity.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  submitEditSenderIdentity(): void {
    const identity = this.editTarget;
    if (!identity || !this.canUpdate) {
      return;
    }

    this.saving = true;
    this.modalErrorMessage = '';

    const payload: UpdateSenderIdentityPayload = {
      name: this.form.name.trim(),
      brandId: this.normalizedBrandId() || undefined,
    };

    this.api
      .updateSenderIdentity(identity.id, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedIdentity) => {
          this.senderIdentities = this.senderIdentities.map((item) => (item.id === updatedIdentity.id ? updatedIdentity : item));
          this.feedbackMessage = `Sender identity updated for ${updatedIdentity.email}.`;
          this.closeModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.modalErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to update sender identity.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  confirmDelete(): void {
    const identity = this.deleteTarget;
    if (!identity || this.deleting) {
      return;
    }

    this.deleting = true;
    this.errorMessage = '';

    this.api
      .deleteSenderIdentity(identity.id)
      .pipe(
        finalize(() => {
          this.deleting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.senderIdentities = this.senderIdentities.filter((item) => item.id !== identity.id);
          this.feedbackMessage = `Sender identity deleted for ${identity.email}.`;
          this.deleteTarget = null;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to delete sender identity.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  trackBySenderIdentity(_: number, identity: SenderIdentitySummary): string {
    return identity.id;
  }

  isResending(identityId: string): boolean {
    return this.resendingIdentityId === identityId;
  }

  protected cloneNotificationSettings(settings: SenderIdentityNotificationSettings): SenderIdentityNotificationSettings {
    return {
      enabled: settings.enabled,
      notifications: {
        ...settings.notifications,
      },
    };
  }

  private normalizedBrandId(): string {
    return this.form.brandId.trim();
  }

  private resolveBrandId(identity: SenderIdentitySummary): string {
    const brandLabel = identity.brand.trim();
    if (!brandLabel || brandLabel === '-') {
      return '';
    }

    return this.brands.find((brand) => brand.name === brandLabel)?.brandId ?? '';
  }

  private loadSenderIdentities(): void {
    this.loading = true;
    this.errorMessage = '';

    this.api
      .listSenderIdentities()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.senderIdentities = response.items;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load sender identities.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private loadBrands(): void {
    this.brandsLoading = true;

    this.brandsApi
      .listBrands()
      .pipe(
        finalize(() => {
          this.brandsLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.brands = response.items;
          if (this.modalMode === 'edit' && this.editTarget && !this.form.brandId) {
            this.form.brandId = this.resolveBrandId(this.editTarget);
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.brands = [];
          this.cdr.markForCheck();
        },
      });
  }
}
